import {
  Home,
  Hammer,
  Building2,
  Briefcase,
  IndianRupee,
  BadgePercent,
  ClipboardList,
  Clock3,
  ShieldCheck,
  FileText,
  Landmark,
  Ruler,
  Wrench,
  User,
  Users,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Construction Loan",
      path: "/construction-loan",
    },
  ]),
];
/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Construction Loan | Money Mart",

  description:
    "Build your dream home with Construction Loans from leading Banks & NBFCs. Get competitive interest rates, flexible repayment options, and expert assistance from Money Mart.",

  keywords:
    "Construction Loan, Home Construction Loan, House Construction Loan, Self Construction Loan, Building Loan, Money Mart",

  canonical: "/construction-loan",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Home Construction Finance Experts",

  title: "Build Your Dream Home with the Right Construction Loan",

  description:
    "Whether you're building a new house on your own plot or constructing an independent home, Money Mart helps you compare Construction Loan offers from leading Banks and NBFCs with expert guidance from application to final disbursement.",

  highlights: [
    "Finance for Self Construction",
    "Competitive Interest Rates",
    "Stage-wise Loan Disbursement",
    "Expert Assistance Throughout the Process",
  ],

  image: "/images/loans/construction_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Construction Loan",

  title: "Finance the Construction of Your Dream Home",

  description:
    "A Construction Loan provides funding to build a residential property on your own land. Funds are typically released in stages based on construction progress, helping you manage your project efficiently.",

  items: [
    {
      icon: Home,
      title: "Build on Your Own Plot",
      description:
        "Construct your dream home on a plot that you already own with first tranche up to 30% of loan amount, subject to lender eligibility. ",
    },
    {
      icon: Hammer,
      title: "Stage-wise Disbursement",
      description:
        "Loan amounts are released in phases as construction progresses, reducing unnecessary interest costs.",
    },
    {
      icon: IndianRupee,
      title: "High Loan Eligibility",
      description:
        "Secure funding based on your income, repayment capacity, and construction estimate.",
    },
    {
      icon: Building2,
      title: "Independent House Construction",
      description:
        "Finance the construction of villas, bungalows, duplexes, and independent residential homes.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Construction Loan?",

  description:
    "Construction Loans provide flexible financing for building your own home while allowing funds to be disbursed according to the progress of construction.",

  items: [
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Compare multiple Banks and NBFCs to secure attractive construction loan interest rates.",
    },
    {
      icon: Hammer,
      title: "Stage-wise Loan Disbursement",
      description:
        "Receive funds in phases aligned with the progress of your construction project.",
    },
    {
      icon: ClipboardList,
      title: "Flexible Repayment Options",
      description:
        "Choose repayment terms that suit your financial goals and construction timeline.",
    },
    {
      icon: Clock3,
      title: "Faster Processing",
      description:
        "Our experts coordinate documentation and lender communication for a smoother approval process.",
    },
    {
      icon: ShieldCheck,
      title: "Expert Construction Loan Guidance",
      description:
        "Get end-to-end assistance from application and document verification to loan disbursement.",
    },
    {
      icon: FileText,
      title: "Transparent Process",
      description:
        "We help you understand lender requirements, construction milestones, and documentation before you begin.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Construction Loan Solutions",

  title: "Construction Loans We Help Arrange",

  description:
    "Whether you're building a new house or expanding an existing residential property, Money Mart helps you find the right construction financing solution.",

  items: [
    {
      icon: Home,
      title: "Self Construction Loan",
      description:
        "Finance the construction of a new residential house on your own plot of land.",
    },
    {
      icon: Building2,
      title: "Independent House Construction",
      description:
        "Build villas, bungalows, duplexes, and independent residential homes with structured financing.",
    },
    {
      icon: Ruler,
      title: "Home Extension & Expansion",
      description:
        "Finance approved residential extensions, additional floors, and major structural expansions where supported by lenders.",
    },
    {
      icon: Wrench,
      title: "Renovation with Construction",
      description:
        "Some lenders offer financing for substantial reconstruction or redevelopment along with construction work, subject to eligibility.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Construction Loan Eligibility",

  description:
    "Eligibility depends on your income, repayment capacity, credit profile, land ownership, and the estimated cost of construction.",

  requirements: [
    {
      icon: User,
      title: "Age",
      value: "21 – 70 Years",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Salaried, Self-Employed, Business Owner or Professional",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Stable & Verifiable Source of Income",
    },
    {
      icon: Home,
      title: "Land Ownership",
      value: "Own a Residential Plot Eligible for Construction",
    },
  ],

  approvalFactors: [
    "Income & repayment capacity",
    "Credit score & repayment history",
    "Ownership of the residential plot",
    "Approved building plan",
    "Estimated construction cost",
    "Existing financial liabilities",
  ],

  cta: {
    title: "Planning to build your dream home?",

    description:
      "Our experts will assess your eligibility and help you choose the right Construction Loan from leading Banks and NBFCs.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Construction Loan",

  description:
    "The exact document requirements vary depending on the lender and applicant profile. These are the documents commonly required for most Construction Loan applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "1 Passport Size Photograph (Applicant & Co-applicant)",
        "PAN Card (Applicant & Co-applicant)",
        "Latest Aadhaar Card (Applicant & Co-applicant)",
      ],
    },
    {
      icon: Briefcase,
      title: "Additional Documents for Salaried Applicants",

      documents: [
        "Last 3 Months Salary Slips",
        "Last 2 Years Form 16",
        "Salary & Savings Account Statements for the Last 6–7 Months",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Additional Documents for Self-Employed Applicants",

      documents: [
        "GST Registration (if applicable)",
        "UDYAM Registration (if applicable)",
        "Last 3 Years ITR with Balance Sheet & Profit & Loss Statement",
        "Business Bank Statements",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Home,
      title: "Land & Property Documents",

      documents: [
        "Ownership Documents of the Plot",
        "Latest Property Tax Receipt",
        "Approved Building Plan",
        "Building Approval from Local Authority",
      ],
    },
    {
      icon: Hammer,
      title: "Construction Documents",

      documents: [
        "Construction Estimate / Cost Sheet",
        "Architect's Approved Plan",
        "Construction Agreement (if applicable)",
        "Contractor Details (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Encumbrance Certificate (if required)",
        "NOCs from Relevant Authorities (where applicable)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Construction Loan in 6 Simple Steps",

  description:
    "From eligibility assessment and document verification to stage-wise loan disbursement, our experts guide you throughout your home construction journey.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Construction Loan?",

  description:
    "Building a home involves multiple approvals, documents, and lender requirements. Our experts simplify the process by helping you compare lenders, prepare documentation, and secure the most suitable Construction Loan.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Construction Loan FAQs",

  description:
    "Find answers to the most commonly asked questions about Construction Loans.",

  items: [
    {
      question: "What is a Construction Loan?",

      answer:
        "A Construction Loan is a home loan designed to finance the construction of a residential property on land owned by the applicant. The loan is typically disbursed in stages as construction progresses.",
    },
    {
      question: "Can I get a Construction Loan if I already own the land?",

      answer:
        "Yes. Construction Loans are specifically meant for individuals who already own a residential plot and wish to build a house on it, subject to lender eligibility criteria.",
    },
    {
      question: "How is a Construction Loan disbursed?",

      answer:
        "Unlike a regular Home Loan, Construction Loans are generally released in multiple stages based on the progress of construction and verification by the lender.",
    },
    {
      question: "Can I use my own contractor or architect?",

      answer:
        "Yes. Most lenders allow you to choose your own contractor or architect, provided the construction plans and estimates meet their requirements.",
    },
    {
      question: "How much loan can I get?",

      answer:
        "The eligible loan amount depends on factors such as your income, repayment capacity, land value, estimated construction cost, and lender policies.",
    },
    {
      question: "Can I prepay my Construction Loan?",

      answer:
        "Yes. Most lenders allow partial or full prepayment, though terms may vary depending on the lender and loan type.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Ready to Build Your Dream Home?",

  title: "Let's Find the Right Construction Loan for You",

  description:
    "Compare Construction Loan offers from leading Banks and NBFCs and receive expert guidance from planning your home to the final loan disbursement.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Construction Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Construction Loan Consultation",

  title: "Speak to a Construction Loan Expert",

  description:
    "Our home finance specialists will compare multiple lenders and help you secure the most suitable Construction Loan for your project.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our Construction Loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const constructionLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default constructionLoan;
